import React from 'react';

interface FilterDrawerProps {
  isOpen: boolean;
  title?: string;
  onClose: () => void;
  onApply?: () => void;
  onClear?: () => void;
  widthClassName?: string; // e.g., 'w-[360px]'
  children: React.ReactNode;
}

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  title = 'Filters',
  onClose,
  onApply,
  onClear,
  widthClassName = 'w-[360px]',
  children,
}) => {
  React.useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  return (
    <div className={`fixed inset-0 z-[300] ${isOpen ? '' : 'pointer-events-none'}`} aria-hidden={!isOpen}>
      {/* Overlay */}
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />

      {/* Panel */}
      <aside
        className={`absolute right-0 top-0 h-full ${widthClassName} bg-white dark:bg-surface-dark-100 border-l border-border-100 dark:border-border-dark-100 shadow-2xl transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-100 dark:border-border-dark-100">
          <h2 className="text-sm font-semibold text-text-primary dark:text-text-primary">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-background-100 dark:hover:bg-surface-dark-200 text-text-secondary dark:text-text-secondary"
            aria-label="Close filters"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 8.586l4.95-4.95a1 1 0 111.414 1.415L11.414 10l4.95 4.95a1 1 0 11-1.414 1.414L10 11.414l-4.95 4.95a1 1 0 01-1.414-1.414L8.586 10l-4.95-4.95A1 1 0 115.05 3.636L10 8.586z" clipRule="evenodd"/></svg>
          </button>
        </div>

        <div className="h-[calc(100%-104px)] overflow-y-auto px-4 py-3">
          {children}
        </div>

        <div className="px-4 py-3 border-t border-border-100 dark:border-border-dark-100 flex items-center justify-between">
          <button
            onClick={onClear}
            className="text-xs px-3 py-2 rounded-md border border-border-100 dark:border-border-dark-100 text-text-secondary dark:text-text-secondary hover:bg-background-100 dark:hover:bg-surface-dark-200"
          >
            Clear
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-xs px-3 py-2 rounded-md border border-border-100 dark:border-border-dark-100 text-text-secondary dark:text-text-secondary hover:bg-background-100 dark:hover:bg-surface-dark-200"
            >
              Close
            </button>
            {onApply && (
              <button
                onClick={onApply}
                className="text-xs px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white"
              >
                Apply
              </button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
};

export default FilterDrawer;


